import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useToast,
} from '@ais-ui/ui-core';
import { BsLayers } from 'react-icons/bs';
import {
  PiArrowClockwiseThin,
  PiFlowerThin,
  PiStar,
  PiStarFill,
} from 'react-icons/pi';
import {
  isWmsLayerShownOnStartUp,
  ToggleWmsLayerInSettings,
  useWMSSettings,
} from '../../WMSLayers/useWMSSettings';
import { WMSDeleteService } from './WMSDeleteService';
import { WMSAddEditService } from './WMSAddEditService';
import { useLocalization } from '@ais-ui/locale-management';
import { NoItemsEmptyStateList } from '../../../components/EmptyList';
import React from 'react';
import { useSettings } from '../SettingsProvider';
import { useSaveSettings, WMSInfo, WMSLayerInfo } from '@ais-ui/websockets';
import {
  addWmsToFavoritesWithOrdering,
  removeWmsFromFavoritesWithOrdering,
} from '../../Map/LayersOnStart/layerOrdering';

interface WMSLayerListProps {
  wmsInfo: WMSInfo;
  wmsLayerInfo: WMSLayerInfo;
}

const WMSLayerList = ({ wmsInfo, wmsLayerInfo }: WMSLayerListProps) => {
  const { messages } = useLocalization();
  const { settings } = useSettings();
  const wmsSettings = useWMSSettings();
  const isShownOnStartup = isWmsLayerShownOnStartUp(settings, wmsLayerInfo.id);
  const { mutate: mutateSettings } = useSaveSettings();
  const { toast } = useToast();

  const onShowOnStartup = () => {
    // Use the helper function that updates both WMS favorites and layer ordering
    const settingsToSave = addWmsToFavoritesWithOrdering(
      settings,
      wmsLayerInfo.id
    );

    mutateSettings(settingsToSave, {
      onSuccess: () => {
        toast({
          className: 'enable-wms-layer-success-toast',
          title: messages.genericToastTitleSuccess,
          description: messages.mapMenuWMSEnabledLayer.replace(
            '{label}',
            wmsLayerInfo.title
          ),
        });
      },
      onError: () => {
        toast({
          className: 'enable-wms-layer-error-toast',
          title: messages.genericToastTitleError,
          description: messages.mapMenuWMSFailedToEnableLayer.replace(
            '{label}',
            wmsLayerInfo.title
          ),
          variant: 'destructive',
        });
      },
    });
  };

  const onRemoveOnStartup = () => {
    // Use the helper function that removes from both WMS favorites and layer ordering
    const settingsToSave = removeWmsFromFavoritesWithOrdering(
      settings,
      wmsLayerInfo.id
    );

    mutateSettings(settingsToSave, {
      onSuccess: () => {
        toast({
          className: 'enable-wms-layer-success-toast',
          title: messages.genericToastTitleSuccess,
          description: messages.mapMenuWMSDisabledLayer.replace(
            '{label}',
            wmsLayerInfo.title
          ),
        });
      },
      onError: () => {
        toast({
          className: 'enable-wms-layer-error-toast',
          title: messages.genericToastTitleError,
          description: messages.mapMenuWMSFailedToDisableLayer.replace(
            '{label}',
            wmsLayerInfo.title
          ),
          variant: 'destructive',
        });
      },
    });
  };

  return (
    <div key={wmsLayerInfo.title} className="pl-4 flex items-center">
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            size="smIcon"
            variant="ghost"
            onClick={isShownOnStartup ? onRemoveOnStartup : onShowOnStartup}
          >
            {isShownOnStartup ? (
              <PiStarFill className="h-4 w-4 text-yellow-400" />
            ) : (
              <PiStar className="h-4 w-4" />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          {isShownOnStartup ? 'No longer show on start up' : 'Show on start up'}
        </TooltipContent>
      </Tooltip>
      <p className="pl-4 text-sm leading-none">{wmsLayerInfo.title}</p>
    </div>
  );
};

export const WMSListServices = () => {
  const { messages } = useLocalization();
  const wmsSettings = useWMSSettings();

  return (
    <Accordion type="single" className="mt-8 space-y-8" collapsible>
      {wmsSettings.wmsInfos.length === 0 ? (
        <NoItemsEmptyStateList
          entity="web map service"
          srcImage="empty_list_bro.png"
        >
          <WMSAddEditService
            mode={'create'}
            className={'w-[80%] h-10'}
          ></WMSAddEditService>
        </NoItemsEmptyStateList>
      ) : (
        wmsSettings.wmsInfos.map((wmsLayer) => (
          <AccordionItem key={wmsLayer.id} value={`item-${wmsLayer.id}`}>
            <AccordionTrigger className="group hover:no-underline">
              <div className="flex items-center">
                <BsLayers className="h-9 w-9" />
                <div className="ml-4 space-y-1">
                  <p className="text-sm text-left font-medium leading-none group-hover:underline">
                    {wmsLayer.name}
                  </p>
                  <p className="text-sm text-left text-muted-foreground">
                    {wmsLayer.url}
                  </p>
                </div>
              </div>
            </AccordionTrigger>

            <AccordionContent>
              <div className="space-y-8">
                <div className="pl-6 mt-4 space-y-4">
                  <h3 className="text-md text-muted-foreground">Styling</h3>
                  <div className="pl-4 flex items-center">
                    <PiFlowerThin className="h-4 w-4"></PiFlowerThin>
                    <p className="pl-4 text-sm leading-none">
                      {messages.settingsWmsServicesTransparencyValue.replace(
                        '{label}',
                        (wmsLayer.transparency as number).toString()
                      )}
                    </p>
                  </div>

                  <div className="pl-4 flex items-center">
                    <PiArrowClockwiseThin className="h-4 w-4"></PiArrowClockwiseThin>
                    <p className="pl-4 text-sm leading-none">
                      {wmsLayer.refreshrate === 0
                        ? messages.settingsWmsServicesRefreshRateAlways
                        : messages.settingsWmsServicesRefreshRateValue.replace(
                            '{label}',
                            (wmsLayer.refreshrate as number).toString()
                          )}
                    </p>
                  </div>
                </div>
                <div className="pl-6 mt-4 space-y-4">
                  <h3 className="text-md text-muted-foreground">
                    {messages.settingsWmsServicesAvailableLayersLabel}
                  </h3>
                  {wmsLayer.layers?.map((layer) => (
                    <WMSLayerList
                      key={layer.id}
                      wmsInfo={wmsLayer}
                      wmsLayerInfo={layer}
                    ></WMSLayerList>
                  ))}
                </div>
                <div className="flex justify-between px-4">
                  <WMSDeleteService wmsLayer={wmsLayer}></WMSDeleteService>
                  <WMSAddEditService
                    existingWmsLayer={wmsLayer}
                    mode="edit"
                  ></WMSAddEditService>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        ))
      )}
    </Accordion>
  );
};
